/**
 * レビュー画面の材料表から材料の名前・分量・単位・備考・説明(prep_note)を直す(#230)。
 *
 * 分量は質問が残っているあいだしか入力欄が出ず、一度埋まると直す手段が
 * 「マニュアルを編集」(#203)にしか無かった。記録はその画面と同じ manualEdits
 * (target=ingredient, key=本文の材料名)で、本文へは「回答を本文に反映」で resolve が当てる。
 */
import { useState } from "react";
import { deleteDoc, serverTimestamp, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth";
import { useI18n } from "../lib/i18n";
import {
  INGREDIENT_FIELDS,
  editDocId,
  ingredientEditError,
  ingredientFieldValue,
  type EditDoc,
  type EditableIngredientFields,
  type IngredientField,
} from "../lib/manual-edits";

interface Props {
  dishId: string;
  /** 本文の材料(反映前の値。編集はここからの差分として持つ) */
  ingredient: EditableIngredientFields;
  /** 他の材料の名前(同じ名前への変更を止める。修正後の名前で渡す) */
  otherNames: string[];
  /** 記録済みの編集(manualEdits 全件) */
  edits: Record<string, EditDoc>;
  onClose: () => void;
}

export function IngredientEditPanel({ dishId, ingredient, otherNames, edits, onClose }: Props) {
  const { user, paths } = useAuth();
  const { t } = useI18n();
  const [drafts, setDrafts] = useState<Partial<Record<IngredientField, string>>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const labels: Record<IngredientField, { label: string; placeholder?: string }> = {
    name: { label: t("名前") },
    quantity: { label: t("分量（数値）"), placeholder: t("例: 225") },
    unit: { label: t("単位"), placeholder: t("例: g") },
    note: { label: t("備考"), placeholder: t("例: 8切れ") },
    prep_note: { label: t("説明（下処理・何から作るか）"), placeholder: t("例: 光中杏・砂糖・水から作ったもの") },
  };
  const idOf = (f: IngredientField) => editDocId("ingredient", ingredient.name, f);
  const currentOf = (f: IngredientField) => ingredientFieldValue(ingredient, f);
  const savedOf = (f: IngredientField) => edits[idOf(f)]?.value ?? currentOf(f);
  const valueOf = (f: IngredientField) => drafts[f] ?? savedOf(f);
  const dirty = INGREDIENT_FIELDS.filter((f) => drafts[f] !== undefined && drafts[f] !== savedOf(f));
  const values = Object.fromEntries(INGREDIENT_FIELDS.map((f) => [f, valueOf(f)])) as Record<IngredientField, string>;
  const invalid = ingredientEditError(values, ingredient.name, otherNames);

  async function save() {
    if (!user || !paths || dirty.length === 0 || invalid) return;
    setSaving(true);
    setError(null);
    try {
      const batch = writeBatch(db);
      for (const f of dirty) {
        const ref = paths.manualEditDoc(dishId, idOf(f));
        const value = (drafts[f] ?? "").trim();
        // 元の本文と同じに戻したら記録そのものを消す(差分だけを持つ。#203 と同じ)
        if (value === currentOf(f)) batch.delete(ref);
        else
          batch.set(ref, {
            target: "ingredient",
            key: ingredient.name,
            field: f,
            value,
            editedBy: user.uid,
            editedAt: serverTimestamp(),
          });
      }
      await batch.commit();
      setDrafts({});
      onClose();
    } catch (e) {
      setError(`${t("保存に失敗しました:")} ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  async function revert(f: IngredientField) {
    if (!paths) return;
    setSaving(true);
    try {
      await deleteDoc(paths.manualEditDoc(dishId, idOf(f)));
      setDrafts((d) => {
        const next = { ...d };
        delete next[f];
        return next;
      });
    } catch (e) {
      setError(`${t("取り消しに失敗しました:")} ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  const fieldOf = (f: IngredientField) => {
    const edited = !!edits[idOf(f)];
    const isDirty = dirty.includes(f);
    return (
      <div className={`edit-field${edited ? " edited" : ""}${isDirty ? " dirty" : ""}`} key={f}>
        <label>
          {labels[f].label}
          {edited && <span className="badge p-medium">{t("編集済み")}</span>}
          {edited && (
            <button className="link" disabled={saving} onClick={() => void revert(f)}>
              {t("元に戻す")}
            </button>
          )}
        </label>
        {f === "prep_note" ? (
          <textarea
            rows={2}
            value={valueOf(f)}
            placeholder={labels[f].placeholder}
            onChange={(e) => setDrafts((d) => ({ ...d, [f]: e.target.value }))}
          />
        ) : (
          <input
            type="text"
            value={valueOf(f)}
            placeholder={labels[f].placeholder}
            onChange={(e) => setDrafts((d) => ({ ...d, [f]: e.target.value }))}
          />
        )}
        {f === "prep_note" && (
          <p className="muted small">{t("材料表で「— 」のあとに出る説明です。空にして保存すると説明を消します。")}</p>
        )}
      </div>
    );
  };

  return (
    <div className="raw-panel ingredient-edit">
      <p className="muted small">
        {t("「{name}」の名前・分量を直します。保存すると記録され、品目の画面の「回答を本文に反映」でマニュアルに反映されます。作り直しても引き継がれます。", { name: ingredient.name })}
      </p>
      <div className="edit-row">
        {fieldOf("name")}
        {fieldOf("quantity")}
        {fieldOf("unit")}
      </div>
      {fieldOf("note")}
      {fieldOf("prep_note")}
      {dirty.length > 0 && invalid && <p className="error small">{t(invalid)}</p>}
      {error && <p className="error">{error}</p>}
      <div className="step-edit-actions">
        <button onClick={() => void save()} disabled={saving || dirty.length === 0 || !!invalid}>
          {saving ? t("保存中…") : t("修正を保存")}
        </button>
        <button className="link" onClick={onClose} disabled={saving}>
          {dirty.length > 0 ? t("保存せずに閉じる") : t("閉じる")}
        </button>
      </div>
    </div>
  );
}
